/**
 * We sometimes need to disable links to dbtCloud, so its important all links to
 * dbtCloud out of Explorer are declared in this file.
 */
import {
  DataWarehouseObjectHandoffQueryParams,
  DbtObjectHandoffParams,
  encodeQueryPageObjectHandoffURL,
  encodeVisualEditorEditExistingObjectHandoffURL,
  encodeVisualEditorNewFromObjectHandoffURL,
} from '@dbt-labs/integrations/productHandoffs/productHandoffURIs';

import {
  AccountDomainParams,
  AccountParams,
  ConnectionParams,
  EnvironmentIdParams,
  IdeDeeplinkParams,
  JobParams,
  LineageIntegrationParams,
  LinkGenerator,
  ProjectParams,
  RunParams,
} from './linkTypes';

export const accountHome: LinkGenerator<AccountParams & AccountDomainParams> = (
  params,
) => {
  return `${params.domain}/a/${params.accountId}`;
};

export const projectDashboard: LinkGenerator<AccountParams & ProjectParams> = (
  params,
) => {
  const origin = window.location.origin;
  return `${origin}/dashboard/${params.accountId}/projects/${params.projectId}/`;
};

export const jobDetails: LinkGenerator<AccountParams & ProjectParams & JobParams> = (
  params,
) => {
  const origin = window.location.origin;
  return `${origin}/deploy/${params.accountId}/projects/${params.projectId}/jobs/${params.jobId}/`;
};

export const runDetails: LinkGenerator<AccountParams & ProjectParams & RunParams> = (
  params,
) => {
  const origin = window.location.origin;
  return `${origin}/deploy/${params.accountId}/projects/${params.projectId}/runs/${params.runId}/`;
};

/**
 * https://cloud.getdbt.com/settings/accounts/:accountId/pages/projects/:projectId
 */
export const projectSettings: LinkGenerator<AccountParams & ProjectParams> = (
  params,
) => {
  return `${origin}/settings/accounts/${params.accountId}/pages/projects/${params.projectId}`;
};

/**
 * https://cloud.getdbt.com/settings/accounts/:accountId/pages/account
 */
export const accountSettings: LinkGenerator<AccountParams> = (params) => {
  return `${origin}/settings/accounts/${params.accountId}/pages/account`;
};

/**
 * https://cloud.getdbt.com/settings/accounts/:accountId/pages/connections
 */
export const accountConnections: LinkGenerator<AccountParams> = (params) => {
  return `${origin}/settings/accounts/${params.accountId}/pages/connections`;
};

/**
 * https://cloud.getdbt.com/settings/accounts/:accountId/pages/connections/:connectionId
 */
export const connectionDetails: LinkGenerator<AccountParams & ConnectionParams> = (
  params,
) => {
  return `${origin}/settings/accounts/${params.accountId}/pages/connections/${params.connectionId}`;
};

/**
 * https://cloud.getdbt.com/settings/accounts/:accountId/pages/projects/:projectId/lineage-integrations/:integrationId
 */
export const lineageIntegrationSettings: LinkGenerator<
  AccountParams & ProjectParams & LineageIntegrationParams
> = (params) => {
  if (!params.lineageIntegrationId) {
    return `${origin}/settings/accounts/${params.accountId}/pages/projects/${params.projectId}/lineage-integrations/new`;
  }
  return `${origin}/settings/accounts/${params.accountId}/pages/projects/${params.projectId}/lineage-integrations/${params.lineageIntegrationId}`;
};

/**
 * https://cloud.getdbt.com/deploy/:accountId/projects/:projectId/environments
 */
export const environmentSettings: LinkGenerator<AccountParams & ProjectParams> = (
  params,
) => {
  return `${origin}/deploy/${params.accountId}/projects/${params.projectId}/environments`;
};

/**
 * https://cloud.getdbt.com/deploy/1/projects/672/environments/672/settings
 */
export const environmentDetailSettings: LinkGenerator<
  AccountParams & ProjectParams & EnvironmentIdParams
> = (params) => {
  return `${origin}/deploy/${params.accountId}/projects/${params.projectId}/environments/${params.environmentId}/settings`;
};
/**
 * https://cloud.getdbt.com/studio/:accountId/projects/:projectId?file=:file
 */
export const cloudIde: LinkGenerator<
  AccountParams & ProjectParams & IdeDeeplinkParams
> = (params) => {
  const { origin } = window.location;
  const baseUrl = `${origin}/studio/${params.accountId}/projects/${params.projectId}`;

  const searchParams = new URLSearchParams(params.file?.map((file) => ['file', file]));
  const paramString = searchParams.toString();

  return paramString ? `${baseUrl}?${paramString}` : baseUrl;
};

/**
 * https://cloud.getdbt.com/query/:accountId/projects/:projectId/explore#model=:uniqueId&environmentId=:environmentId
 */
export const insightsDbt: LinkGenerator<
  AccountParams & ProjectParams & DbtObjectHandoffParams
> = (params) => {
  const { origin } = window.location;

  const handoffParams = {
    environmentId: params.environmentId,
    objectType: params.objectType,
    objectId: params.objectId,
  };
  const encodedHandoffURLFragment = encodeQueryPageObjectHandoffURL(
    {
      referrer: 'explore',
      accountId: params.accountId,
      projectId: params.projectId,
    },
    handoffParams,
  );

  return `${origin}${encodedHandoffURLFragment.toString()}`;
};

/**
 * https://cloud.getdbt.com/query/:accountId/projects/:projectId/explore#dwh_table=:urn&environmentId=:environmentId&warehouse=:warehouseType
 */
export const insightsDWH: LinkGenerator<
  AccountParams & ProjectParams & DataWarehouseObjectHandoffQueryParams
> = (params) => {
  const { origin } = window.location;

  const handoffParams = {
    environmentId: params.environmentId,
    objectType: params.objectType,
    objectId: params.objectId,
    warehouseType: params.warehouseType,
  };
  const encodedHandoffURLFragment = encodeQueryPageObjectHandoffURL(
    {
      referrer: 'explore',
      accountId: params.accountId,
      projectId: params.projectId,
    },
    handoffParams,
  );

  return `${origin}${encodedHandoffURLFragment.toString()}`;
};

/**
 * https://cloud.getdbt.com/develop-ui/canvas/:accountId/projects/handoff/new-from#model=:uniqueId&environmentId=:environmentId
 */
export const canvasDbtCreate: LinkGenerator<
  AccountParams & ProjectParams & DbtObjectHandoffParams
> = (params) => {
  const { origin } = window.location;

  const handoffParams = {
    environmentId: params.environmentId,
    objectType: params.objectType,
    objectId: params.objectId,
  };
  const encodedHandoffURLFragment = encodeVisualEditorNewFromObjectHandoffURL(
    {
      accountId: params.accountId,
      projectId: params.projectId,
    },
    handoffParams,
  );

  return `${origin}${encodedHandoffURLFragment.toString()}`;
};

/**
 * https://cloud.getdbt.com/develop-ui/canvas/:accountId/projects/:projectId/handoff/edit-existing#model=:uniqueId&environmentId=:environmentId
 */
export const canvasDbtEdit: LinkGenerator<
  AccountParams & ProjectParams & DbtObjectHandoffParams
> = (params) => {
  const { origin } = window.location;

  const handoffParams = {
    environmentId: params.environmentId,
    objectType: params.objectType,
    objectId: params.objectId,
  };
  const encodedHandoffURLFragment = encodeVisualEditorEditExistingObjectHandoffURL(
    {
      accountId: params.accountId,
      projectId: params.projectId,
    },
    handoffParams,
  );

  return `${origin}${encodedHandoffURLFragment.toString()}`;
};

/**
 * https://cloud.getdbt.com/query/:accountId/projects/:projectId/explore#dwh_table=:urn&environmentId=:environmentId&warehouse=:warehouseType
 */
export const canvasDWHCreate: LinkGenerator<
  AccountParams & ProjectParams & DataWarehouseObjectHandoffQueryParams
> = (params) => {
  const { origin } = window.location;

  const handoffParams = {
    environmentId: params.environmentId,
    objectType: params.objectType,
    objectId: params.objectId,
    warehouseType: params.warehouseType,
  };
  const encodedHandoffURLFragment = encodeVisualEditorNewFromObjectHandoffURL(
    {
      accountId: params.accountId,
      projectId: params.projectId,
    },
    handoffParams,
  );

  return `${origin}${encodedHandoffURLFragment.toString()}`;
};

export const canvasDWHEdit: LinkGenerator<
  AccountParams & ProjectParams & DataWarehouseObjectHandoffQueryParams
> = (params) => {
  const { origin } = window.location;

  const handoffParams = {
    environmentId: params.environmentId,
    objectType: params.objectType,
    objectId: params.objectId,
    warehouseType: params.warehouseType,
  };
  const encodedHandoffURLFragment = encodeVisualEditorEditExistingObjectHandoffURL(
    {
      accountId: params.accountId,
      projectId: params.projectId,
    },
    handoffParams,
  );

  return `${origin}${encodedHandoffURLFragment.toString()}`;
};

export const serviceToken: LinkGenerator<AccountParams> = (params) => {
  const { origin } = window.location;
  return `${origin}/settings/accounts/${params.accountId}/pages/service-tokens`;
};
